import { post } from '@/lib/api'
import { clearTokenData, getRefreshToken, getToken, storeTokenData } from '@/lib/token/token'

type ExchangeResponse = {
  token: string
  refresh_token?: string
  expires_in?: number
}

export const SessionStorageKeys = {
  /**
   * Loop guard: set before redirecting to the silent login and whenever a silent attempt has
   * already failed, so a miss cannot bounce the browser between the SPA and Keycloak forever.
   * sessionStorage deliberately - it survives the same-tab round-trip to Keycloak and back, but
   * neither follows into new tabs nor gets wiped by the app's localStorage.clear() calls.
   */
  SILENT_LOGIN_ATTEMPTED: 'oidc_silent_login_attempted',
  /**
   * The /login shim's loop-breaker: while set, a return to /login renders the retry state instead
   * of redirecting to Keycloak again, so a failing exchange or callback cannot bounce the browser
   * between the SPA and Keycloak. Only a successful exchange or a deliberate sign-out clears it.
   */
  INTERACTIVE_LOGIN_ATTEMPTED: 'oidc_interactive_login_attempted',
  /** Where the user was before being sent to Keycloak. Client-side only, never sent anywhere. */
  RETURN_URL: 'oidc_return_url',
  /**
   * Rolling count of interactive redirects, as `{ count, firstAt }`. Deliberately NOT cleared by a
   * successful exchange, which is what separates it from INTERACTIVE_LOGIN_ATTEMPTED: in a
   * re-authentication loop every exchange succeeds, so any guard reset on that event can never
   * accumulate and never fires. This one decays only with time, or when the user explicitly
   * retries, so it caps a loop no matter what is causing it.
   */
  LOGIN_BOUNCES: 'oidc_login_bounces',
} as const

/** The router mounts under /v2, but this module runs before it and deals in raw URLs. */
const BASE_PATH = '/v2'

/** Interactive redirects allowed inside BOUNCE_WINDOW_MS before /login stops and shows the retry state. */
const BOUNCE_LIMIT = 3

/** How long a run of bounces stays "recent". Long enough to catch a loop, short enough that ordinary
 * sign-in, sign-out, sign-in traffic never trips it. */
const BOUNCE_WINDOW_MS = 60_000

const LOGIN_URL = `${BASE_PATH}/login`

/** Routes a logged-out user may legitimately be on: silent login must never hijack these. */
const LOGGED_OUT_PATHS = [
  '/login',
  '/two-factor',
  '/forgot-password',
  '/reset-password',
  '/registration-completion',
  // The terminal stop for an unresolvable Keycloak identity. Silent auth must never fire here:
  // the Keycloak session is live, so it would succeed, fail identity again, and loop.
  '/no-account',
]

export const isOidcEnabled = (): boolean => import.meta.env.VITE_KEYCLOAK_OIDC_ENABLED === 'true'

const isOnLoggedOutPath = (pathname: string): boolean =>
  LOGGED_OUT_PATHS.some(path => pathname.startsWith(`${BASE_PATH}${path}`))

/**
 * Never settles. Returned once a top-level navigation away from the app has started, so the
 * caller does not render a frame that is about to be unloaded.
 */
const navigationInProgress = (): Promise<never> => new Promise<never>(() => {})

export const hasAttemptedInteractiveLogin = (): boolean =>
  !!sessionStorage.getItem(SessionStorageKeys.INTERACTIVE_LOGIN_ATTEMPTED)

/**
 * For a sign-out the user chose: the attempt that set these guards is over, so the next /login
 * should go to Keycloak rather than park on the retry state. The bounce record stays, because it
 * caps loops by time, not by outcome.
 */
export const clearLoginAttempts = (): void => {
  sessionStorage.removeItem(SessionStorageKeys.SILENT_LOGIN_ATTEMPTED)
  sessionStorage.removeItem(SessionStorageKeys.INTERACTIVE_LOGIN_ATTEMPTED)
}

/**
 * Sends the browser to Keycloak's interactive sign-in, marking the attempt first so a failed
 * round-trip parks on the retry state instead of redirecting again.
 */
export const redirectToInteractiveLogin = (): Promise<never> => {
  sessionStorage.setItem(SessionStorageKeys.INTERACTIVE_LOGIN_ATTEMPTED, '1')
  recordLoginBounce()

  window.location.href = `${import.meta.env.VITE_USER_MANAGER_URL}/auth/oidc/login`

  return navigationInProgress()
}

/** Reads the bounce record, discarding one that has aged out of the window. */
const readLoginBounces = (): { count: number; firstAt: number } | null => {
  try {
    const raw = sessionStorage.getItem(SessionStorageKeys.LOGIN_BOUNCES)

    if (raw === null) {
      return null
    }

    const parsed: unknown = JSON.parse(raw)

    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      typeof (parsed as { count?: unknown }).count !== 'number' ||
      typeof (parsed as { firstAt?: unknown }).firstAt !== 'number'
    ) {
      return null
    }

    const record = parsed as { count: number; firstAt: number }

    return Date.now() - record.firstAt > BOUNCE_WINDOW_MS ? null : record
  } catch {
    // Unreadable or unavailable storage must never block a login attempt.
    return null
  }
}

/** Counts one interactive redirect, starting a fresh window if the last run has aged out. */
const recordLoginBounce = (): void => {
  try {
    const current = readLoginBounces()

    sessionStorage.setItem(
      SessionStorageKeys.LOGIN_BOUNCES,
      JSON.stringify(
        current === null
          ? { count: 1, firstAt: Date.now() }
          : { count: current.count + 1, firstAt: current.firstAt }
      )
    )
  } catch {
    // Storage is a convenience here, not a correctness requirement.
  }
}

/**
 * True once the browser has bounced through interactive login too often in quick succession, which
 * means something is failing after the exchange rather than at it. The /login shim stops redirecting
 * and shows its retry state instead, so a loop costs a handful of round trips rather than running
 * until a rate limiter refuses it.
 */
export const hasExceededLoginBounces = (): boolean => (readLoginBounces()?.count ?? 0) >= BOUNCE_LIMIT

/**
 * Keeps the `?redirect` target across the cross-origin round-trip, so the exchange can restore it.
 *
 * Runs on first entry to /login and again on an explicit retry: a failed exchange hands the target
 * back as that page's `?redirect` param, so a bare retry would otherwise land on the post-login
 * default instead of where the user was going.
 */
export const stashReturnUrl = (): void => {
  const redirectParam = new URLSearchParams(window.location.search).get('redirect')

  if (redirectParam === null) {
    return
  }

  // `redirect` is router-relative, RETURN_URL is a window path. Restore the basepath on the way in,
  // or the exchange's replaceUrl lands on /enquiries rather than /v2/enquiries.
  sessionStorage.setItem(SessionStorageKeys.RETURN_URL, withBasePath(redirectParam))
}

/**
 * The retry screen's action. Distinct from the automatic redirect in that it clears the bounce cap
 * first: a click is a fresh intent to sign in, whereas the cap exists to stop unattended cycling.
 */
export const retryInteractiveLogin = (): void => {
  stashReturnUrl()

  clearLoginBounces()

  void redirectToInteractiveLogin()
}

/** Clears the bounce record. Only for a deliberate user retry, which is a fresh intent to sign in. */
export const clearLoginBounces = (): void => {
  try {
    sessionStorage.removeItem(SessionStorageKeys.LOGIN_BOUNCES)
  } catch {
    // Nothing to do; an unreadable store simply means no record to clear.
  }
}

/**
 * Replaces the current history entry so the one-time `?code` never survives into history,
 * bookmarks or referrer headers.
 */
const replaceUrl = (url: string): void => window.history.replaceState(null, '', url)

const scrubbedCurrentUrl = (): string => {
  const url = new URL(window.location.href)

  url.searchParams.delete('code')
  url.searchParams.delete('login_required')

  return `${url.pathname}${url.search}${url.hash}`
}

/** The stashed pre-redirect location, consumed on read. Includes the /v2 basepath. */
const takeReturnUrl = (): string | null => {
  const returnUrl = sessionStorage.getItem(SessionStorageKeys.RETURN_URL)

  sessionStorage.removeItem(SessionStorageKeys.RETURN_URL)

  return returnUrl
}

/**
 * Drops the leading `/v2`. The stashed return URL is a window path, but the login page hands
 * `redirect` to the router's `navigate({ to })`, and the router is mounted with `basepath: 'v2'`,
 * so it prefixes the basepath itself. Passing the window path through doubles it to `/v2/v2/...`
 * and the destination is lost. `_auth.tsx` already writes this param router-relative.
 */
const withoutBasePath = (url: string): string =>
  url.startsWith(BASE_PATH) ? url.slice(BASE_PATH.length) || '/' : url

/** Same shape endSession() uses, so the login page restores the location after a password login. */
/**
 * The inverse of `withoutBasePath`, for the /login shim. `redirect` is router-relative by the
 * reasoning above, but RETURN_URL is a window path - bootstrap step 3 writes `pathname + search`
 * and `replaceUrl` hands it to `history.replaceState`. The shim reads one and writes the other, so
 * without this the basepath is dropped and a restored deep link loses its `/v2`.
 */
export const withBasePath = (url: string): string =>
  url.startsWith(BASE_PATH) ? url : `${BASE_PATH}${url === '/' ? '' : url}`

const loginUrlWithRedirect = (returnUrl: string | null): string =>
  returnUrl ? `${LOGIN_URL}?redirect=${encodeURIComponent(withoutBasePath(returnUrl))}` : LOGIN_URL

/**
 * Swaps the single-use handoff code for a token pair. The code in the body is the credential,
 * so no vf-token header; `post()` unwraps the user-manager's `{ data: ... }` envelope.
 */
const exchangeCode = (code: string): Promise<ExchangeResponse> =>
  post('/auth/exchange', { code }, import.meta.env.VITE_USER_MANAGER_URL, false)

/**
 * OIDC bootstrap state machine. Runs once per cold load, before the router exists, reading the
 * raw URL because the root search schema strips params it does not know about (`?code` and
 * `?login_required` included) before any route could see them.
 *
 *   1. `?code` - the handoff from a successful (silent or interactive) Keycloak login. Exchange
 *      it, store the pair on the existing token seam, scrub the URL, restore where the user was.
 *   2. `?login_required=1` - a silent attempt found no Keycloak session. Remember that in the
 *      loop guard and land on the interactive login.
 *   3. Truly logged out (no access and no refresh token) - redirect to the silent login, at most
 *      once per tab, never from pages a logged-out user is meant to be on.
 *   4. Anything else - the app behaves exactly as it does today.
 *
 * Resolves when the app should render; never resolves once it has navigated away.
 */
export const runOidcBootstrap = async (): Promise<void> => {
  const params = new URLSearchParams(window.location.search)
  const code = params.get('code')

  // Step 1: consume the one-time handoff code.
  if (code) {
    try {
      const data = await exchangeCode(code)

      storeTokenData({ token: data.token, refresh_token: data.refresh_token })

      sessionStorage.removeItem(SessionStorageKeys.SILENT_LOGIN_ATTEMPTED)
      sessionStorage.removeItem(SessionStorageKeys.INTERACTIVE_LOGIN_ATTEMPTED)

      replaceUrl(takeReturnUrl() ?? scrubbedCurrentUrl())
    } catch {
      // Read storage now rather than trusting what was here on entry: a sibling tab may have
      // completed its own exchange while ours was in flight, and a stray `?code` can land on a
      // tab that already holds a working session. Either way the session here is not one this
      // failure created, so tearing it down would log the user out over a spent URL param. The
      // stashed return URL is left alone for the same reason. The loop guards are not: the
      // session is live, so no login attempt is still outstanding, and leaving them set parks a
      // later visit to /login on the retry line instead of sending it to Keycloak.
      if (getToken() || getRefreshToken()) {
        sessionStorage.removeItem(SessionStorageKeys.SILENT_LOGIN_ATTEMPTED)
        sessionStorage.removeItem(SessionStorageKeys.INTERACTIVE_LOGIN_ATTEMPTED)
        replaceUrl(scrubbedCurrentUrl())

        return
      }

      // No session came out of this (spent or unknown code, rate limit, network). Guard against
      // an immediate silent re-bounce and land on the interactive login instead.
      clearTokenData()
      sessionStorage.setItem(SessionStorageKeys.SILENT_LOGIN_ATTEMPTED, '1')
      replaceUrl(loginUrlWithRedirect(takeReturnUrl()))
    }

    return
  }

  // Step 2: a silent attempt already failed upstream - go interactive, do not bounce again.
  if (params.get('login_required')) {
    sessionStorage.setItem(SessionStorageKeys.SILENT_LOGIN_ATTEMPTED, '1')
    replaceUrl(loginUrlWithRedirect(takeReturnUrl()))

    return
  }

  // Step 3: truly logged out on a protected page - try the silent login, once per tab.
  if (
    isOidcEnabled() &&
    !getToken() &&
    !getRefreshToken() &&
    !sessionStorage.getItem(SessionStorageKeys.SILENT_LOGIN_ATTEMPTED) &&
    !isOnLoggedOutPath(window.location.pathname)
  ) {
    sessionStorage.setItem(
      SessionStorageKeys.RETURN_URL,
      `${window.location.pathname}${window.location.search}`
    )
    sessionStorage.setItem(SessionStorageKeys.SILENT_LOGIN_ATTEMPTED, '1')

    window.location.href = `${import.meta.env.VITE_USER_MANAGER_URL}/auth/oidc/login?silent=1`

    return navigationInProgress()
  }

  // Step 4: proceed as today.
}
